import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  X,
  Smartphone,
  BarChart3,
  Zap,
  ShoppingCart,
  Settings,
  Clock,
  CheckCircle,
  Star,
  MessageCircle,
  ArrowRight,
  Sparkles,
  TrendingUp,
  Users,
  Package,
  Bot,
  Printer,
  Brain,
  MessageSquare,
  Shield,
  Headphones,
  QrCode,
  Utensils,
  CreditCard,
  Bell,
  LayoutDashboard,
  Palette,
  Gift,
  Play,
  ChevronRight,
  Check,
  Rocket,
  Heart
} from 'lucide-react';
import { Button } from './components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './components/ui/accordion';
import { Card, CardContent } from './components/ui/card';

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeFeatureTab, setActiveFeatureTab] = useState(0);
  const [visibleSections, setVisibleSections] = useState({});

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('section[id]').forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const aiFeatures = [
    {
      icon: Bot,
      title: 'Atendente Virtual 24/7',
      description: 'IA treinada para atender seus clientes automaticamente, tirando dúvidas e coletando pedidos mesmo fora do horário comercial.'
    },
    {
      icon: MessageSquare,
      title: 'Conversação Natural',
      description: 'Entende mensagens de texto, áudio e até imagens. Responde de forma humanizada, como um atendente real.'
    },
    {
      icon: Printer,
      title: 'Impressão Automática',
      description: 'Pedidos confirmados são enviados instantaneamente para sua impressora térmica. Zero trabalho manual!'
    },
    {
      icon: Brain,
      title: 'Aprendizado Contínuo',
      description: 'A IA aprende com cada interação, melhorando o atendimento e sugerindo produtos baseado no histórico do cliente.'
    }
  ];

  const mainFeatures = [
    {
      icon: Smartphone,
      title: 'Cardápio Digital Responsivo',
      description: 'Design elegante que funciona perfeitamente em qualquer dispositivo. Seus clientes navegam com facilidade.',
      color: '#3b82f6'
    },
    {
      icon: QrCode,
      title: 'QR Code Personalizado',
      description: 'Gere QR Codes únicos para mesas, balcão ou delivery. Cada cliente acessa seu cardápio em segundos.',
      color: '#8b5cf6'
    },
    {
      icon: ShoppingCart,
      title: 'Gestão de Pedidos em Tempo Real',
      description: 'Acompanhe todos os pedidos em um painel intuitivo. Notificações instantâneas para não perder nada.',
      color: '#f59e0b'
    },
    {
      icon: LayoutDashboard,
      title: 'Painel Administrativo Completo',
      description: 'Controle produtos, categorias, preços, horários e muito mais. Tudo em um único lugar.',
      color: '#10b981'
    },
    {
      icon: BarChart3,
      title: 'Relatórios e Analytics',
      description: 'Entenda seu negócio com dados reais. Produtos mais vendidos, horários de pico, ticket médio e mais.',
      color: '#ef4444'
    },
    {
      icon: Palette,
      title: 'Personalização Total',
      description: 'Cores, logo, banner, categorias... Deixe seu cardápio com a cara do seu negócio.',
      color: '#ec4899'
    },
    {
      icon: Bell,
      title: 'Notificações Inteligentes',
      description: 'Sons e alertas para novos pedidos. Nunca mais perca uma venda por distração.',
      color: '#06b6d4'
    },
    {
      icon: Utensils,
      title: 'Gestão de Mesas',
      description: 'Controle de comandas por mesa, abertura e fechamento de contas, divisão de conta.',
      color: '#84cc16'
    },
    {
      icon: CreditCard,
      title: 'Integração Pagamentos',
      description: 'Aceite PIX, cartões e dinheiro. Controle total sobre formas de pagamento.',
      color: '#f97316'
    },
    {
      icon: Gift,
      title: 'Cupons e Promoções',
      description: 'Crie cupons de desconto, promoções especiais e fidelize seus clientes.',
      color: '#a855f7'
    },
    {
      icon: Clock,
      title: 'Horários Flexíveis',
      description: 'Configure horários de funcionamento, pausas e dias especiais automaticamente.',
      color: '#14b8a6'
    },
    {
      icon: Shield,
      title: 'Segurança Total',
      description: 'Dados protegidos com criptografia. Backups automáticos e acesso seguro.',
      color: '#6366f1'
    }
  ];

  const plans = [
    {
      name: 'Essencial',
      price: '97,90',
      description: 'Tudo que você precisa para digitalizar seu negócio',
      features: [
        'Cardápio digital completo',
        'Produtos ilimitados',
        'Gestão de pedidos',
        'QR Code personalizado',
        'Painel administrativo',
        'Impressão automática',
        'Múltiplas impressoras',
        'Relatórios completos',
        'Gestão de mesas',
        'Cupons e promoções',
        'Suporte por WhatsApp'
      ],
      notIncluded: [
        'IA Atendente WhatsApp',
        'Suporte prioritário'
      ],
      popular: false,
      cta: 'Começar Agora'
    },
    {
      name: 'Premium',
      price: '147,90',
      description: 'Automação completa com Inteligência Artificial',
      features: [
        'Tudo do Essencial +',
        'IA Atendente WhatsApp 24h',
        'Atendimento automático',
        'Pedidos por voz no WhatsApp',
        'Suporte prioritário',
        'Treinamento incluso',
        'Atualizações antecipadas'
      ],
      notIncluded: [],
      popular: true,
      cta: 'Quero o Premium',
      highlight: 'MAIS VENDIDO'
    }
  ];

  const testimonials = [
    {
      name: 'Roberto Mendes',
      business: 'Hamburgueria do Betão',
      city: 'São Paulo, SP',
      text: 'A IA do WhatsApp mudou completamente meu negócio! Antes eu perdia pedidos de madrugada, agora o robô atende e manda direto pra impressora. Faturei 40% a mais no primeiro mês!',
      rating: 5,
      image: null
    },
    {
      name: 'Fernanda Costa',
      business: 'Açaí da Fê',
      city: 'Belo Horizonte, MG',
      text: 'Simplesmente sensacional! Meus clientes amam a facilidade do cardápio digital. E a impressão automática é um sonho, não preciso mais ficar copiando pedido.',
      rating: 5,
      image: null
    },
    {
      name: 'Carlos Alberto',
      business: 'Pizzaria Bella Massa',
      city: 'Curitiba, PR',
      text: 'Testei vários sistemas e esse é disparado o melhor. O suporte é incrível, me ajudaram a configurar tudo. A IA entende até áudio do cliente!',
      rating: 5,
      image: null
    },
    {
      name: 'Mariana Silva',
      business: 'Doceria Amor Doce',
      city: 'Rio de Janeiro, RJ',
      text: 'Consegui reduzir erros de pedido a quase zero. Antes anotava errado, agora vai tudo certinho pro papel. Super recomendo!',
      rating: 5,
      image: null
    }
  ];

  const faqs = [
    {
      question: 'Como funciona a IA que atende no WhatsApp?',
      answer: 'Nossa inteligência artificial é treinada especificamente para atendimento em restaurantes. Ela recebe as mensagens dos clientes no WhatsApp, entende o pedido (texto, áudio ou imagem), tira dúvidas sobre o cardápio, confirma o pedido e envia automaticamente para impressão. Funciona 24 horas por dia, 7 dias por semana!'
    },
    {
      question: 'Preciso de uma impressora especial?',
      answer: 'A função de impressão automática funciona com impressoras térmicas de 58mm ou 80mm conectadas à rede WiFi ou via cabo USB em um computador. Recomendamos modelos como Epson TM-T20 ou similares. Se você já tem uma impressora térmica, provavelmente vai funcionar!'
    },
    {
      question: 'E se a IA não entender o cliente?',
      answer: 'Nossa IA é muito inteligente, mas em casos complexos ela direciona a conversa para você. Você pode assumir o atendimento a qualquer momento pelo próprio WhatsApp. Além disso, ela aprende com cada interação e fica mais precisa com o tempo.'
    },
    {
      question: 'Posso testar antes de assinar?',
      answer: 'Sim! Oferecemos 15 dias de teste grátis completo, com todas as funcionalidades do plano Premium. Você não precisa cadastrar cartão e pode cancelar quando quiser. Sem compromisso!'
    },
    {
      question: 'Como meus clientes acessam o cardápio?',
      answer: 'Seus clientes podem acessar via link direto, QR Code na mesa, ou pelo próprio WhatsApp quando a IA envia o cardápio. É super fácil e funciona em qualquer celular, sem precisar baixar nenhum aplicativo.'
    },
    {
      question: 'Quanto tempo leva para configurar tudo?',
      answer: 'A configuração básica leva menos de 30 minutos. Você cadastra seus produtos, personaliza as cores e já pode usar. Para a integração com WhatsApp e impressora, nossa equipe ajuda você gratuitamente no suporte.'
    },
    {
      question: 'Posso cancelar a qualquer momento?',
      answer: 'Sim, sem multa e sem burocracia. Você pode cancelar pelo próprio painel ou pelo WhatsApp do suporte. Seus dados ficam salvos por 30 dias caso queira voltar.'
    },
    {
      question: 'Funciona para delivery e mesa?',
      answer: 'Sim! O sistema é completo e funciona para delivery, retirada no balcão e consumo no local com gestão de mesas. Você controla tudo pelo mesmo painel.'
    }
  ];

  const stats = [
    { value: '2.500+', label: 'Estabelecimentos ativos', icon: Users },
    { value: '180k+', label: 'Pedidos processados/mês', icon: Package },
    { value: '99.9%', label: 'Uptime garantido', icon: Shield },
    { value: '4.9', label: 'Avaliação dos clientes', icon: Star }
  ];

  return (
    <div className="landing-page-v2">
      {/* Header */}
      <header className={`lp-header ${scrolled ? 'scrolled' : ''}`}>
        <div className="lp-container">
          <div className="header-inner">
            <div className="logo-area" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <img 
                src="/logo.png" 
                alt="Meu Cardápio Zap" 
                className="logo-image"
              />
              <span className="logo-text">Meu Cardápio Zap</span>
            </div>

            <nav className="nav-desktop">
              <button onClick={() => scrollToSection('ai-section')} className="nav-item">
                IA WhatsApp
              </button>
              <button onClick={() => scrollToSection('features')} className="nav-item">
                Funcionalidades
              </button>
              <button onClick={() => scrollToSection('pricing')} className="nav-item">
                Preços
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="nav-item">
                Depoimentos
              </button>
              <button onClick={() => scrollToSection('faq')} className="nav-item">
                FAQ
              </button>
            </nav>

            <div className="header-actions">
              <Button variant="ghost" onClick={() => navigate('/login')} className="btn-login">
                Entrar
              </Button>
              <Button onClick={() => navigate('/login')} className="btn-signup">
                Teste Grátis
              </Button>
            </div>

            <button className="mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="mobile-nav">
              <button onClick={() => scrollToSection('ai-section')}>IA WhatsApp</button>
              <button onClick={() => scrollToSection('features')}>Funcionalidades</button>
              <button onClick={() => scrollToSection('pricing')}>Preços</button>
              <button onClick={() => scrollToSection('testimonials')}>Depoimentos</button>
              <button onClick={() => scrollToSection('faq')}>FAQ</button>
              <div className="mobile-nav-actions">
                <Button variant="outline" onClick={() => navigate('/login')} className="w-full">
                  Entrar
                </Button>
                <Button onClick={() => navigate('/login')} className="w-full">
                  Teste Grátis 15 Dias
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section-v2">
        <div className="hero-bg">
          <div className="hero-gradient-1"></div>
          <div className="hero-gradient-2"></div>
          <div className="hero-particles"></div>
        </div>
        
        <div className="lp-container hero-content-v2">
          <div className="hero-left">
            <div className="hero-badge animate-fade-in">
              <Bot size={16} />
              <span>Revolucionário: IA + WhatsApp + Impressora</span>
              <Sparkles size={14} className="sparkle" />
            </div>
            
            <h1 className="hero-title-v2 animate-fade-in-up">
              Seu Atendente de 
              <span className="gradient-text"> Inteligência Artificial</span>
              <br />que Nunca Dorme
            </h1>
            
            <p className="hero-subtitle animate-fade-in-up delay-1">
              O único cardápio digital com <strong>IA que atende seus clientes no WhatsApp</strong> e 
              envia os pedidos <strong>direto para sua impressora</strong>. Automatize seu negócio 
              e venda mais, mesmo enquanto você dorme.
            </p>

            <div className="hero-features-mini animate-fade-in-up delay-2">
              <div className="mini-feature">
                <Check size={18} />
                <span>Atendimento automático 24h</span>
              </div>
              <div className="mini-feature">
                <Check size={18} />
                <span>Impressão instantânea</span>
              </div>
              <div className="mini-feature">
                <Check size={18} />
                <span>Melhor custo-benefício do mercado</span>
              </div>
            </div>
            
            <div className="hero-cta-group animate-fade-in-up delay-3">
              <Button size="lg" onClick={() => navigate('/login')} className="btn-primary-hero">
                <Rocket size={20} />
                Testar Grátis por 15 Dias
              </Button>
              <Button size="lg" variant="outline" onClick={() => scrollToSection('ai-section')} className="btn-secondary-hero">
                <Play size={18} />
                Ver como funciona
              </Button>
            </div>

            <div className="hero-trust animate-fade-in-up delay-4">
              <span className="trust-text">Sem cartão de crédito • Cancele quando quiser</span>
            </div>
          </div>

          <div className="hero-right animate-float">
            <div className="hero-visual">
              <div className="phone-mockup">
                <div className="phone-screen">
                  <div className="chat-simulation">
                    <div className="chat-msg user">
                      <span>Quero um hambúrguer artesanal com bacon e fritas</span>
                    </div>
                    <div className="chat-msg bot">
                      <div className="bot-avatar">
                        <Bot size={14} />
                      </div>
                      <span>Ótima escolha! 🍔 Confirmando seu pedido:
                        <br /><br />
                        • 1x Hambúrguer Artesanal com Bacon
                        <br />• 1x Porção de Fritas
                        <br /><br />
                        Total: R$ 38,90
                        <br /><br />
                        Forma de pagamento?</span>
                    </div>
                    <div className="chat-msg user">
                      <span>PIX</span>
                    </div>
                    <div className="chat-msg bot">
                      <div className="bot-avatar">
                        <Bot size={14} />
                      </div>
                      <span>Perfeito! ✅ Pedido confirmado!
                        <br />Tempo estimado: 25 min
                        <br /><br />
                        📄 Enviando para impressora...</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="printer-mockup">
                <div className="printer-body">
                  <Printer size={24} />
                </div>
                <div className="printer-paper">
                  <div className="receipt">
                    <span className="receipt-title">PEDIDO #247</span>
                    <span>1x Hambúrguer Artesanal</span>
                    <span>+ Bacon</span>
                    <span>1x Fritas</span>
                    <span className="receipt-total">R$ 38,90 - PIX</span>
                  </div>
                </div>
              </div>

              <div className="floating-badge badge-1">
                <MessageCircle size={16} />
                <span>WhatsApp</span>
              </div>
              <div className="floating-badge badge-2">
                <Bot size={16} />
                <span>IA Ativa</span>
              </div>
              <div className="floating-badge badge-3">
                <Printer size={16} />
                <span>Imprimindo...</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="stats-bar">
        <div className="lp-container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item-v2">
                <stat.icon className="stat-icon-v2" size={24} />
                <div className="stat-content">
                  <span className="stat-value-v2">{stat.value}</span>
                  <span className="stat-label-v2">{stat.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Section - Hero Feature */}
      <section id="ai-section" className={`ai-section ${visibleSections['ai-section'] ? 'visible' : ''}`}>
        <div className="lp-container">
          <div className="section-header-v2">
            <div className="section-badge">
              <Sparkles size={14} />
              <span>Exclusivo Meu Cardápio Zap</span>
            </div>
            <h2 className="section-title-v2">
              A Revolução do Atendimento
              <span className="gradient-text"> Chegou</span>
            </h2>
            <p className="section-desc-v2">
              Imagine ter um atendente que trabalha 24 horas, nunca erra pedido, 
              não pede férias e ainda manda tudo direto para a cozinha. Isso é o nosso sistema de IA.
            </p>
          </div>

          <div className="ai-features-grid">
            {aiFeatures.map((feature, index) => (
              <div key={index} className="ai-feature-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="ai-feature-icon">
                  <feature.icon size={28} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="ai-demo-section">
            <div className="ai-demo-content">
              <h3>Veja na Prática</h3>
              <p>
                A IA entende linguagem natural, gírias, abreviações e até áudios. 
                Ela identifica o que o cliente quer, sugere adicionais, aplica cupons 
                e fecha o pedido em segundos.
              </p>
              <ul className="ai-benefits-list">
                <li>
                  <Check size={18} />
                  <span>Atende múltiplos clientes simultaneamente</span>
                </li>
                <li>
                  <Check size={18} />
                  <span>Nunca esquece de oferecer adicionais</span>
                </li>
                <li>
                  <Check size={18} />
                  <span>Aprende o cardápio e responde dúvidas</span>
                </li>
                <li>
                  <Check size={18} />
                  <span>Impressão automática em segundos</span>
                </li>
                <li>
                  <Check size={18} />
                  <span>Histórico completo de conversas</span>
                </li>
              </ul>
              <Button onClick={() => navigate('/login')} className="btn-ai-demo">
                Quero Testar a IA
                <ArrowRight size={18} />
              </Button>
            </div>
            <div className="ai-demo-visual">
              <div className="workflow-animation">
                <div className="workflow-step step-1">
                  <div className="step-icon"><MessageCircle size={24} /></div>
                  <span>Cliente envia mensagem</span>
                </div>
                <div className="workflow-arrow">
                  <ChevronRight size={20} />
                </div>
                <div className="workflow-step step-2">
                  <div className="step-icon"><Bot size={24} /></div>
                  <span>IA processa e responde</span>
                </div>
                <div className="workflow-arrow">
                  <ChevronRight size={20} />
                </div>
                <div className="workflow-step step-3">
                  <div className="step-icon"><Check size={24} /></div>
                  <span>Pedido confirmado</span>
                </div>
                <div className="workflow-arrow">
                  <ChevronRight size={20} />
                </div>
                <div className="workflow-step step-4">
                  <div className="step-icon"><Printer size={24} /></div>
                  <span>Imprime na cozinha</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className={`features-section-v2 ${visibleSections['features'] ? 'visible' : ''}`}>
        <div className="lp-container">
          <div className="section-header-v2">
            <div className="section-badge">
              <Settings size={14} />
              <span>Funcionalidades Completas</span>
            </div>
            <h2 className="section-title-v2">
              Tudo que Você Precisa em
              <span className="gradient-text"> Um Só Lugar</span>
            </h2>
            <p className="section-desc-v2">
              Além da IA revolucionária, você tem acesso a um sistema completo de gestão 
              para seu restaurante, lanchonete, pizzaria ou delivery.
            </p>
          </div>

          <div className="features-grid-v2">
            {mainFeatures.map((feature, index) => (
              <div 
                key={index} 
                className="feature-card-v2"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="feature-icon-v2" style={{ background: `${feature.color}15`, color: feature.color }}>
                  <feature.icon size={24} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className={`pricing-section-v2 ${visibleSections['pricing'] ? 'visible' : ''}`}>
        <div className="lp-container">
          <div className="section-header-v2">
            <div className="section-badge trial-badge">
              <Gift size={14} />
              <span>15 Dias Grátis</span>
            </div>
            <h2 className="section-title-v2">
              Planos que Cabem no
              <span className="gradient-text"> Seu Bolso</span>
            </h2>
            <p className="section-desc-v2">
              Comece com teste grátis de 15 dias. Sem cartão, sem compromisso. 
              Cancele quando quiser.
            </p>
          </div>

          <div className="pricing-grid-v2">
            {plans.map((plan, index) => (
              <div key={index} className={`pricing-card-v2 ${plan.popular ? 'popular' : ''}`}>
                {plan.highlight && (
                  <div className="pricing-highlight">
                    <Star size={12} />
                    <span>{plan.highlight}</span>
                  </div>
                )}
                <div className="pricing-header">
                  <h3>{plan.name}</h3>
                  <p>{plan.description}</p>
                </div>
                <div className="pricing-price">
                  <span className="currency">R$</span>
                  <span className="amount">{plan.price}</span>
                  <span className="period">/mês</span>
                </div>
                <ul className="pricing-features">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="included">
                      <CheckCircle size={16} />
                      <span>{feature}</span>
                    </li>
                  ))}
                  {plan.notIncluded.map((feature, idx) => (
                    <li key={idx} className="not-included">
                      <X size={16} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => navigate('/login')} 
                  className={plan.popular ? 'btn-pricing-primary' : 'btn-pricing-secondary'}
                  variant={plan.popular ? 'default' : 'outline'}
                >
                  {plan.cta}
                </Button>
                <span className="pricing-trial">15 dias grátis para testar</span>
              </div>
            ))}
          </div>

          <div className="pricing-guarantee">
            <Shield size={24} />
            <div>
              <strong>Garantia de Satisfação</strong>
              <p>Se não gostar em 15 dias, não paga nada. Simples assim.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className={`testimonials-section-v2 ${visibleSections['testimonials'] ? 'visible' : ''}`}>
        <div className="lp-container">
          <div className="section-header-v2">
            <div className="section-badge">
              <Heart size={14} />
              <span>Clientes Satisfeitos</span>
            </div>
            <h2 className="section-title-v2">
              Quem Usa,
              <span className="gradient-text"> Recomenda</span>
            </h2>
            <p className="section-desc-v2">
              Mais de 2.500 estabelecimentos já transformaram seus negócios com o Meu Cardápio Zap.
            </p>
          </div>

          <div className="testimonials-grid-v2">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card-v2" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} fill="#fbbf24" stroke="#fbbf24" />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar-v2">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div className="author-info">
                    <strong>{testimonial.name}</strong>
                    <span>{testimonial.business}</span>
                    <span className="author-city">{testimonial.city}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className={`faq-section-v2 ${visibleSections['faq'] ? 'visible' : ''}`}>
        <div className="lp-container">
          <div className="section-header-v2">
            <div className="section-badge">
              <MessageSquare size={14} />
              <span>Tire Suas Dúvidas</span>
            </div>
            <h2 className="section-title-v2">
              Perguntas
              <span className="gradient-text"> Frequentes</span>
            </h2>
          </div>

          <div className="faq-container-v2">
            <Accordion type="single" collapsible className="faq-accordion-v2">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="faq-item-v2">
                  <AccordionTrigger className="faq-trigger-v2">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="faq-content-v2">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="faq-contact">
            <p>Ainda tem dúvidas?</p>
            <a href="https://wa.me/5538999034380" target="_blank" rel="noopener noreferrer" className="faq-whatsapp">
              <MessageCircle size={20} />
              <span>Fale conosco no WhatsApp</span>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="lp-container">
          <div className="final-cta-content">
            <h2>Pronto para Revolucionar seu Negócio?</h2>
            <p>
              Comece agora com 15 dias grátis e veja na prática como a IA pode 
              transformar seu atendimento e aumentar suas vendas.
            </p>
            <div className="final-cta-buttons">
              <Button size="lg" onClick={() => navigate('/login')} className="btn-final-primary">
                <Rocket size={20} />
                Começar Teste Grátis
              </Button>
            </div>
            <div className="final-trust-badges">
              <span><Check size={16} /> Sem cartão de crédito</span>
              <span><Check size={16} /> Configuração em minutos</span>
              <span><Check size={16} /> Suporte humano</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-v2">
        <div className="lp-container">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo.png" alt="Meu Cardápio Zap" className="footer-logo-img" />
                <span>Meu Cardápio Zap</span>
              </div>
              <p>O cardápio digital com IA que revoluciona seu atendimento.</p>
              <div className="footer-social">
                <a href="https://wa.me/5538999034380" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                  <MessageCircle size={20} />
                </a>
              </div>
            </div>

            <div className="footer-links-grid">
              <div className="footer-column">
                <h4>Produto</h4>
                <button onClick={() => scrollToSection('ai-section')}>IA WhatsApp</button>
                <button onClick={() => scrollToSection('features')}>Funcionalidades</button>
                <button onClick={() => scrollToSection('pricing')}>Preços</button>
              </div>
              <div className="footer-column">
                <h4>Empresa</h4>
                <button onClick={() => scrollToSection('testimonials')}>Depoimentos</button>
                <button onClick={() => scrollToSection('faq')}>FAQ</button>
              </div>
              <div className="footer-column">
                <h4>Suporte</h4>
                <a href="https://wa.me/5538999034380" target="_blank" rel="noopener noreferrer">
                  WhatsApp
                </a>
                <button onClick={() => navigate('/login')}>Área do Cliente</button>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© 2025 Meu Cardápio Zap. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Float */}
      <a
        href="https://wa.me/5538999034380"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float-v2"
        aria-label="Contato WhatsApp"
      >
        <MessageCircle size={28} />
        <span className="whatsapp-tooltip">Fale conosco</span>
      </a>
    </div>
  );
};

export default LandingPage;
